/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useRef, useState } from 'react';
import { ButtonGroup, Card, CardContent, CardHeader, ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper, Typography } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import { InfoIcon, LightTooltip, ArrowRightIcon, DocumentIcon, OverflowTip } from '../../../../components';
import { useStyles } from './styles';
import { useApiError, useAppData, useLoading } from '../../../../providers';
import { getBalance } from '../../../../layouts';
import { PATH_COMMISSIONS_BY_CLIENT } from '../../../../utils';

interface Props {
  translation: any;
  selectedCurrency: string;
}

function getCurrencySymbol(currency: string) {
  return (0)
    .toLocaleString('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
}

export const Balance: FC<Props> = ({ translation, selectedCurrency }) => {
  const classes = useStyles();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const { clientPortalRedirectUrl } = useAppData();
  const [numberPart, setNumberPart] = useState<string>('');
  const [decimalPart, setDecimalPart] = useState<string>('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await getBalance(selectedCurrency || 'USD');
        const currencySymbol = getCurrencySymbol(response.data.currency);
        const balanceAmount = response.data.amount.toString();
        const balanceArr = balanceAmount.match(/^(\d+)(\.\d+)?/);
        if (balanceArr && balanceArr.length > 0) {
          setNumberPart(`${currencySymbol} ${balanceArr[1]}`);
          setDecimalPart(balanceArr[2]);
        }
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const redirect = () => {
    window.open(`${clientPortalRedirectUrl}/payments/withdrawals`, '_blank');
  };

  return (
    <Card className={`${classes.root} ${classes.balance}`}>
      <CardHeader
        action={
          <>
            <ButtonGroup ref={anchorRef} className={classes.buttonGroup}>
              <IconButton className={classes.button} onClick={handleToggle}>
                <MoreVert />
              </IconButton>
            </ButtonGroup>
            <Popper open={open} anchorEl={anchorRef.current} transition disablePortal style={{ zIndex: 6000 }}>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList className={classes.menuList}>
                        <MenuItem onClick={redirect} className={classes.menuItem}>
                          <ArrowRightIcon />
                          <span className={classes.menuItemText}>{translation.withdraw}</span>
                        </MenuItem>
                        <span className={classes.menuTitle}>{translation.text_6663}</span>
                        <MenuItem className={classes.menuItem} component={Link} to={PATH_COMMISSIONS_BY_CLIENT}>
                          <DocumentIcon />
                          <span className={classes.menuItemText}>{translation.commissions_report}</span>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        }
        title={
          <>
            <span>{translation.available_balance}</span>
            <LightTooltip title={translation.balance_tooltip} arrow placement="right-start">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </LightTooltip>
          </>
        }
      />
      <CardContent className={classes.padding}>
        <Typography component="p" className={classes.number}>
          <OverflowTip title={`${numberPart}${decimalPart}`}>
            {numberPart}
            <span className={classes.decimal}>{decimalPart}</span>
          </OverflowTip>
        </Typography>
      </CardContent>
    </Card>
  );
};
